import { PipelineNode, PipelineNodeField, PipelineNodeJoinPath } from "@models/pipelineNode";
import { Badge, Form, Offcanvas } from "react-bootstrap";
import { Updater, useImmer } from "use-immer";
import PipelineNodeSelector from "../PipelineNodeSelector.component";
import { getGroupValueForNodeType } from "@services/modeling.service";
import PipelineNodeColumnSelector from "../PipelineNodeColumnSelector.component";
import { shortid } from "@services/id.service";
import { useCallback, useMemo, useState } from "react";
import { Pane, PaneContent } from "@pages/PageStructure.component";
import PipelineNodeName, { PipelineNodeFieldName } from "../PipelineNodeName.component";
import produce from "immer";
import { useJoinPaths } from "@stores/data.store";
import PipelineNodeJoinPathLabel from "../mapping/PipelineNodeJoinPathLabel.component";
import { SummaryOptionPicker } from "./PipelineNodeColumnCleanerUpper.component";
import { convertToTitleCase } from "@services/formatting.service";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { requireConfirmation } from "@services/alert/alert.service";

interface Props {
    node: PipelineNode;
    onChange: Updater<PipelineNode>;
}

const joinPathKey = (joinPath: PipelineNodeJoinPath[]) => {
    return joinPath.map(jp => {
        return `${jp.object_id}|${jp.object_type}`;
    }).join(' ->');
}

const reorder = <T,>(list: T[], startIndex: number, endIndex: number): T[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
    userSelect: "none" as const,
    display: isDragging ? 'table' : 'table-row',
    backgroundColor: isDragging ? 'var(--ct-light)' : 'inherit',
    ...draggableStyle
});

const PipelineNodeDataMartConfiguration = (props: Props) => {
    const [activeColumnId, setActiveColumnId] = useState('');

    const joinPaths = useJoinPaths(props.node.upstream_node_ids.length > 0 ? props.node.upstream_node_ids[0] : '');
    
    const [showActiveColumn, setShowActiveColumn] = useState(false);

    const [activeColumnForEditing, setActiveColumnForEditing] = useImmer<PipelineNodeField|undefined>(undefined);

    const editColumn = useCallback((columnId: string) => {
        const theColumn = props.node.fields.find(f => f.id === columnId);
        if (!theColumn) {
            return;
        }


        setActiveColumnId(columnId);
        setActiveColumnForEditing(produce(theColumn, draft => {
            return;
        }));
        setShowActiveColumn(true);
    }, [props.node.fields]);

    const saveActiveColumn = useCallback(() => {
        if (!activeColumnForEditing) {
            return;
        }
        props.onChange(draft => {
            // Splice it and replace it with the active column
            const idx = draft.fields.findIndex(f => f.id === activeColumnId);
            if (idx >= 0) {
                draft.fields[idx] = activeColumnForEditing;
            }
        });
        setShowActiveColumn(false);
    }, [activeColumnForEditing, activeColumnId, props.onChange]);

    const activeColumnValidJoinPaths = useMemo(() => {
        if (!activeColumnForEditing) {
            return [];
        }

        const nodeId = activeColumnForEditing.map_options[0]?.source_node_id || '';

        return joinPaths.data?.filter(jp => jp[jp.length - 1].object_id === nodeId) || [];
    }, [activeColumnForEditing]);

    const activeColumnJoinPathKey = useMemo(() => {
        if (!activeColumnForEditing) {
            return '';
        }
        return joinPathKey(activeColumnForEditing.map_options[0]?.join_path || []);
    }, [activeColumnForEditing]);

    const deleteColumn = useCallback(async (columnId: string) => {
        const confirmed = await requireConfirmation('Are you sure you want to delete this column?', 'Delete Column');
        if (!confirmed) {
            return;
        }
        props.onChange(draft => {
            const idx = draft.fields.findIndex(f => f.id === columnId);
            if (idx >= 0) {
                draft.fields.splice(idx, 1);
            }
        });
    }, [props.onChange]);

    const updateRootEntity = useCallback((nodeId: string) => {
        props.onChange(draft => {
            if (nodeId) {
                draft.upstream_node_ids = [nodeId];

            } else {
                draft.upstream_node_ids = [];
            }

            // Also remove the join paths for each field
            draft.fields.forEach(f => {
                f.map_options[0].join_path = [];
            });
        });

        
    }, [props.onChange]);

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        props.onChange(draft => {
            draft.fields = reorder(
                draft.fields,
                result.source.index,
                result.destination!.index
            );
        });
    };

    const addColumn = useCallback(() => {
        const id = shortid();
        const newCol = {
            id: id,
            label: '',
            type: 'STRING',
            taxonomic_id: '',
            cell_actions: [],
            description: '',
            part_of_composite_key: false,
            name: '',
            map_options: [{
                id: shortid(),
                source_node_id: '',
                attribute_id: '',
            }]
        };

        props.onChange(draft => {
            draft.fields.push(newCol);
        });

        setActiveColumnForEditing(newCol);

        setActiveColumnId(id);
        setShowActiveColumn(true);
    }, [props.onChange]);

    return <div>
        <Offcanvas placement="end" show={showActiveColumn} onHide={() => setShowActiveColumn(false)}>
            <Offcanvas.Header>
                <Offcanvas.Title>Column Configuration</Offcanvas.Title>
                <div>
                    <button onClick={saveActiveColumn} className="btn btn-primary btn-sm me-1"><i className="mdi mdi-check-bold"></i> Apply Edits</button>
                    <button className="btn btn-light btn-sm" onClick={() => {
                        setShowActiveColumn(false);
                    }}>
                        <i className="mdi mdi-undo"></i> Cancel
                    </button>
                </div>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <Pane>
                    <PaneContent>
                        {activeColumnForEditing && <>
                            <div className="p-2">
                                <Form.Group className="mb-3">
                                    <Form.Label>Column Name *</Form.Label>
                                    <Form.Control
                                        value={activeColumnForEditing.label}
                                        onChange={(e) => {
                                            setActiveColumnForEditing(draft => {
                                                draft!.label = e.target.value;
                                            });
                                        }}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Column Description</Form.Label>
                                    <Form.Control
                                        value={activeColumnForEditing.description}
                                        as="textarea"
                                        onChange={(e) => {
                                            setActiveColumnForEditing(draft => {
                                                draft!.description = e.target.value;
                                            });
                                        }}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Entity *</Form.Label>
                                    <PipelineNodeSelector
                                        selectedId={activeColumnForEditing?.map_options[0]?.source_node_id || ''}
                                        optionFilter={(option) => {
                                            return getGroupValueForNodeType(option.node_type) == 'DATA_MODELING';
                                        }}
                                        onSelect={(selected) => {
                                            setActiveColumnForEditing(draft => {
                                                if (!draft) {
                                                    return;
                                                }

                                                if (!draft.map_options) {
                                                    draft.map_options = [];
                                                }

                                                draft.map_options[0].source_node_id = selected?.id as string || '';
                                            })
                                            
                                        }}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Column *</Form.Label>
                                    <PipelineNodeColumnSelector
                                        includeRecordId
                                        selectedId={activeColumnForEditing.map_options[0]?.attribute_id || ''}
                                        disabled={!activeColumnForEditing.map_options[0]?.source_node_id}
                                        pipelineNodeId={activeColumnForEditing.map_options[0]?.source_node_id || ''}
                                        onSelect={(col) => {
                                            setActiveColumnForEditing(draft => {
                                                if (!draft) {
                                                    return;
                                                }

                                                
                                                draft.map_options[0].attribute_id = col?.id || '';

                                                // Also set the type
                                                draft.type = col?.type || 'STRING';
                                            });
                                           
                                        }}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Aggregation *</Form.Label>
                                    <SummaryOptionPicker
                                        disabled={!activeColumnForEditing.map_options[0]?.source_node_id || !activeColumnForEditing.map_options[0]?.attribute_id}
                                        fieldType={activeColumnForEditing.type}
                                        aggregator={activeColumnForEditing.merge_behavior || 'PICK_ONE'}
                                        onChangeAggregator={(newAggregator) => {
                                            setActiveColumnForEditing(draft => {
                                                if (!draft) {
                                                    return;
                                                }
                                                draft.merge_behavior = newAggregator;

                                                if (newAggregator == 'GROUP_BY') {
                                                    draft.part_of_composite_key = true;
                                                } else {
                                                    draft.part_of_composite_key = false;
                                                }
                                            });
                                        }}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Join Path *</Form.Label>
                                    <ul className="list-group">
                                        {activeColumnValidJoinPaths.map((jp, jpIdx) => {
                                            const jpKey = joinPathKey(jp);
                                            const selected = activeColumnJoinPathKey == jpKey;
                                            return <li className="list-group-item d-flex center-vertically list-group-item-action" key={jpIdx} onClick={() => {
                                                setActiveColumnForEditing(draft => {
                                                    if (!draft) {
                                                        return;
                                                    }
                                                    draft.map_options[0].join_path = jp;
                                                });
                                            }}>
                                                <div className="me-2 font-18">
                                                    {selected && <i className="mdi mdi-checkbox-marked-circle text-primary"/>}
                                                    {!selected && <i className="mdi mdi-checkbox-blank-circle-outline text-muted"/>}
                                                </div>
                                                <div className="flex-1">
                                                    <PipelineNodeJoinPathLabel joinPath={jp}/>

                                                </div>
                                            </li>
                                        })}
                                    </ul>
                                </Form.Group>
                            </div>
                        </>}
                        
                    </PaneContent>
                </Pane>
            </Offcanvas.Body>
        </Offcanvas>
        <div>
            <h2><i className="mdi mdi-storefront"></i> Configure Datamart</h2>
            <Form.Group className="mb-3">
                <Form.Label>Select Root Entity</Form.Label>
                <PipelineNodeSelector

                    selectedId={props.node.upstream_node_ids.length > 0 ? props.node.upstream_node_ids[0] : ''}
                    onSelect={(selected: PipelineNode | undefined) => {
                        if (selected) {
                            updateRootEntity(selected.id as string);
                        } else {
                            updateRootEntity('');
                        }
                    }}
                    optionFilter={(option) => {
                        return getGroupValueForNodeType(option.node_type) == 'DATA_MODELING';
                    }}
                />
                <Form.Text>The root entity determines how you group your data mart and how other entities can be connected to it.</Form.Text>
            </Form.Group>
            <Form.Label>Columns</Form.Label>
            <DragDropContext onDragEnd={onDragEnd}>
                <table className="table table-fixed table-centered">
                    <thead className="bg-light">
                        <tr>
                            <th style={{width: '5%'}}></th>
                            <th style={{width: '25%'}}>Column</th>
                            <th style={{width: '20%'}}>Source</th>
                            <th style={{width: '20%'}}>Aggregation</th>
                            <th className="text-end" style={{width: '25%'}}>
                                <button className="btn btn-light btn-sm" onClick={addColumn}><i className="mdi mdi-plus-thick"></i> Column</button>
                            </th>
                        </tr>
                    </thead>
                    <Droppable droppableId="droppable">
                        {(provided) => (
                            <tbody
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                            >
                                {props.node.fields.map((f, index) => (
                                    <Draggable key={f.id} draggableId={f.id} index={index}>
                                        {(provided, snapshot) => {
                                            if (provided.draggableProps.style?.transform) {
                                                const transform = provided.draggableProps.style.transform;
                                                const t = transform.split(",")[1];
                                                provided.draggableProps.style.transform = "translate(0px," + t;
                                            }

                                            const hasIssues = !f.label || !f.map_options || !f.map_options.length || !f.map_options[0].source_node_id || !f.map_options[0].attribute_id || !f.map_options[0].join_path || !f.map_options[0].join_path.length;
                                            
                                            return (
                                                <tr
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    style={getItemStyle(
                                                        snapshot.isDragging,
                                                        provided.draggableProps.style
                                                    )}
                                                >
                                                    <td {...provided.dragHandleProps} style={{width: '5%'}}>
                                                        <i className="mdi mdi-drag font-18"></i>
                                                    </td>
                                                    <td style={{width: '25%'}}>
                                                        <h4 className="mb-0">{f.label || 'Unnamed Column'}</h4>
                                                        <div className="text-muted font-13">{f.description || 'No description'}</div>
                                                    </td>
                                                    <td style={{width: '20%'}}>
                                                        <h4 className="mb-0"><PipelineNodeFieldName pipelineNodeId={f.map_options[0]?.source_node_id || ''} fieldId={f.map_options[0]?.attribute_id || ''}/></h4>
                                                        <div className="text-muted font-13">
                                                            From <PipelineNodeName pipelineNodeId={f.map_options[0]?.source_node_id || ''}/>
                                                        </div>
                                                    </td>
                                                    <td style={{width: '20%'}}>
                                                        {f.map_options[0]?.attribute_id && <>
                                                            <Badge className={`${f.part_of_composite_key ? 'bg-pliable' : 'bg-primary'}`}>
                                                                {f.part_of_composite_key ? 'Group By' : convertToTitleCase(f.merge_behavior || 'PICK_ONE')}
                                                            </Badge>
                                                        </>}
                                                    </td>
                                                    <td className="text-end" style={{width: '25%'}}>
                                                        
                                                        {hasIssues && <i className="mdi mdi-alert text-danger font-18 me-1"></i>}
                                                        <button className="btn btn-light btn-sm me-1" title="Edit" onClick={() => {
                                                            editColumn(f.id);
                                                        }}><i className="mdi mdi-cog"></i></button>
                                                        <button className="btn btn-light btn-sm" title="Delete" onClick={() => {
                                                            deleteColumn(f.id);
                                                        }}><i className="mdi mdi-delete"></i></button>
                                                    </td>
                                                </tr>
                                            );
                                        }}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </tbody>
                        )}
                    </Droppable>
                </table>
            </DragDropContext>
        </div>

    </div>
}



export default PipelineNodeDataMartConfiguration;
